import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { cardinalApi, guard } from '../services/auth.api'
import { DocumentApi } from '@icure/cardinal-sdk'

export const documentApiRtk = createApi({
  reducerPath: 'documentApi',
  tagTypes: ['Document'],
  baseQuery: fetchBaseQuery({ baseUrl: '' }),
  endpoints: (builder) => ({
    getAndTryDecryptMainAttachmentAsJson: builder.query<Record<string, string> | undefined, string>({
      // Always return { data } | { error }
      async queryFn(documentId, { getState }) {
        try {
          const docApi: DocumentApi | undefined = (await cardinalApi(getState))?.document
          if (!docApi) {
            return { error: { status: 'NO_DOC_API', data: 'Document API unavailable' } as any }
          }

          const existing = await docApi.getDocument(documentId).catch(() => null)
          if (!existing) {
            // Document missing -> treat as empty data, not error
            return { data: undefined }
          }

          const bytes = await docApi.getAndTryDecryptMainAttachment(existing).catch(() => undefined)
          let json: Record<string, string> | undefined = undefined
          try {
            json = bytes ? JSON.parse(new TextDecoder().decode(bytes)) : undefined
          } catch {
            json = undefined
          }

          // Explicitly normalize to undefined, never null
          return { data: json ?? undefined }
        } catch (e) {
          return { error: { status: 'CUSTOM_ERROR', data: String((e as Error)?.message ?? e) } as any }
        }
      },

      // Drop cache immediately when unused so refetches don’t serve stale data
      keepUnusedDataFor: 0,

      // Always provide a tag for this id (even if data is undefined) so others can invalidate it
      providesTags: (_result, _error, id) => [{ type: 'Document', id }],

      // Optional: for same-arg re-subscribes (e.g., dev StrictMode), force a refetch
      forceRefetch({ currentArg, previousArg }) {
        return currentArg === previousArg
      },
    }),
    getAndTryDecryptMainAttachment: builder.query<Int8Array | undefined, string>({
      async queryFn(documentId, { getState }) {
        const documentApi = (await cardinalApi(getState))?.document
        if (!documentApi) {
          throw new Error('Something went wrong')
        }
        return guard([documentApi], async ([documentApi]): Promise<Int8Array | undefined> => {
          const existingDocument = await documentApi.getDocument(documentId).catch(() => null)
          if (!existingDocument) {
            throw new Error(`Document with id: ${documentId} is not find`)
          }

          return await documentApi.getAndTryDecryptMainAttachment(existingDocument).catch(() => undefined)
        })
      },
      providesTags: (res, error, arg) => (res ? [{ type: 'Document', id: arg }] : []),
    }),
  }),
})

export const { useGetAndTryDecryptMainAttachmentAsJsonQuery, useGetAndTryDecryptMainAttachmentQuery, useLazyGetAndTryDecryptMainAttachmentQuery } = documentApiRtk
